import { z } from 'zod'
import { oc } from '@orpc/contract'
import { artifactsSchema } from '../../model/artifacts/artifacts.schema'
import { detailsMetaSchema } from './patient-clinical-summary-details.contract'

/**
 * Zod schema for the stored ClinicalSummaryOverview, mirroring
 * `model/clinical-summary/overview/types.ts` field-for-field. The drift guard lives in the handler
 * file, NOT here: the model type's import chain reaches middleware-internal `@/` aliases that the
 * web (which bundles *.contract.ts across the package boundary) cannot resolve.
 */
export const clinicalSummaryOverviewSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  artifacts: artifactsSchema,
  generatedAt: z.string(),
  clinicalSummaryDetailsHash: z.string(),
  // Transparency block of the details the overview was built from — present on overviews written
  // since this field was added, absent on older ones. Drives the limitations banner on the overview.
  detailsMeta: detailsMetaSchema.optional(),
  beingSuperseded: z.boolean(),
  beingSupersededSince: z.string().optional(),
})

// Staleness/regeneration state shared by both endpoints:
//   exists          — the patient has a stored overview DocumentReference
//   upToDate        — the stored overview's details hash matches a freshly assembled details hash
//                     (on the GET endpoint it is only computed — and present — when the caller
//                     opts in via checkUpToDate; the refresh endpoint always reports it)
//   beingSuperseded — a replacement overview is being built (advisory flag on the DR)
//   beingSupersededThinking — live "thinking" feed from the in-flight rebuild (advisory,
//                     present only while a build is running and has emitted progress)
//   lastBuildFailed — the last rebuild FAILED (`at` + a short reason, e.g. 'Timed out');
//                     mutually exclusive with beingSuperseded. While present the server
//                     declines non-forced refreshes — only the user's explicit Refresh
//                     retries — and the UI shows "Failed to refresh" in place of the
//                     staleness badge.
const overviewStateShape = {
  exists: z.boolean(),
  upToDate: z.boolean(),
  beingSuperseded: z.boolean(),
  beingSupersededSince: z.string().optional(),
  beingSupersededThinking: z.string().optional(),
  lastBuildFailed: z.object({ at: z.string(), reason: z.string() }).optional(),
}

// GET query params arrive as strings via OpenAPILink and no global coercion
// plugin is configured, so accept both wire forms and normalize to boolean.
const checkUpToDateParam = z
  .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
  .optional()

// Current Clinical Summary Overview for a patient plus its staleness state.
// `clinicalSummaryOverview` is present iff `exists`. Computing `upToDate` means
// assembling the full details dataset to hash it — slow — so it's opt-in via
// `checkUpToDate` (default false), letting the UI paint the stored overview
// fast and verify staleness in a follow-up call.
export const patientClinicalSummaryOverview = oc
  .route({ method: 'GET', path: '/v2/patient/{id}/clinical-summary-overview' })
  .input(z.object({ id: z.string(), checkUpToDate: checkUpToDateParam }))
  .output(
    z.object({
      clinicalSummaryOverview: clinicalSummaryOverviewSchema.optional(),
      ...overviewStateShape,
      upToDate: z.boolean().optional(),
    }),
  )

// Kick off an asynchronous overview rebuild unless the stored one is already up
// to date (`force` rebuilds regardless) or a build is already in flight. Admin
// route: the build runs under the Medplum ClientApplication identity.
export const patientClinicalSummaryOverviewRefresh = oc
  .route({ method: 'POST', path: '/v2/patient/{id}/clinical-summary-overview/refresh' })
  .input(z.object({ id: z.string(), force: z.boolean().optional() }))
  .output(z.object(overviewStateShape))
